import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { UploadCloud, Loader2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GalleryUpload({ value = [], onChange, disabled = false, max = 8, testid = "gallery-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    if ((value?.length || 0) + list.length > max) { toast.error(`You can add up to ${max} photos.`); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      {value?.length > 0 && (
        <div className="mb-3 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid={`${testid}-list`}>
          {value.map((url, i) => (
            <div key={i} className="group relative overflow-hidden rounded-lg border border-ff-border" data-testid={`${testid}-item-${i}`}>
              <img src={url} alt={`Gallery ${i + 1}`} className="aspect-square w-full object-cover" />
              {!disabled && (
                <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)}
                  className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-ff-red shadow-sm hover:bg-white">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!disabled && (value?.length || 0) < max && (
        <div onClick={() => !uploading && inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-ff-border bg-[#F3F4F6] p-6 text-center transition-colors duration-200 hover:border-ff-blue">
          <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" data-testid={`${testid}-input`}
            onChange={(e) => uploadFiles(e.target.files)} />
          {uploading ? (
            <><Loader2 className="mb-2 h-6 w-6 animate-spin text-ff-blue" /><span className="text-sm font-semibold text-ff-blue">Uploading…</span></>
          ) : (
            <><UploadCloud className="mb-2 h-6 w-6 text-ff-medium" />
              <span className="text-sm font-semibold text-ff-blue">Add more photos</span>
              <span className="mt-1 text-xs text-ff-medium">JPG, PNG, GIF or WebP · up to {max} photos</span></>
          )}
        </div>
      )}
      {disabled && (value?.length || 0) === 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-ff-border bg-[#F3F4F6] p-3 text-sm text-ff-medium">
          <ImageIcon className="h-4 w-4" /> No additional photos.
        </div>
      )}
    </div>
  );
}
